import { PublicClientApplication } from '@azure/msal-browser';

// Microsoft 365 (Entra) -kirjautuminen MSAL:lla, aina redirect-flowna (popup ei toimi luotettavasti
// mobiilissa). App-rekisteröinti on monitenant (azure/tf), joten authority on `organizations`
// (työ- ja koulutilit). Backend päättää, kuka pääsee sisään (login.sso.tenant-domains).
const clientId = process.env.VUE_APP_AZURE_CLIENT_ID || '';
const authority = process.env.VUE_APP_AZURE_AUTHORITY || 'https://login.microsoftonline.com/organizations';

export const azureSsoEnabled = !!clientId;

const loginRequest = { scopes: ['openid', 'profile', 'email'] };

let instance = null;
let initPromise = null;

function getInstance() {
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        // Redirect URI ei saa sisältää fragmenttia ja sovellus käyttää hash-reititystä, joten palataan
        // juureen. Vastaus (#code=...) käsitellään paikallaan handleMsalRedirectissa ennen Vuen
        // käynnistystä; paluureitti kulkee state-parametrissa.
        redirectUri: window.location.origin + '/',
        navigateToLoginRequestUrl: false,
      },
      cache: { cacheLocation: 'localStorage' },
    });
  }
  return instance;
}

function initMsal() {
  if (!initPromise) {
    initPromise = getInstance().initialize();
  }
  return initPromise;
}

// Vain tämän sovelluksen tilit (id_tokenin aud): MSAL:n välimuistissa samalla originilla voi olla myös
// muiden sovellusten Microsoft-tilejä.
function findAccount(msal, homeAccountId) {
  const accounts = msal.getAllAccounts().filter(a => a.idTokenClaims && a.idTokenClaims.aud === clientId);
  return accounts.find(a => a.homeAccountId === homeAccountId) || accounts[0] || null;
}

function toLoginResult(result) {
  // Backendille lähetetään id_token, joten sen oma exp ratkaisee. result.expiresOn on
  // Graph-access-tokenin vanhenemisaika (60-90 min), joka on id_tokenia (60 min) pidempi.
  const idTokenExp = result.idTokenClaims && result.idTokenClaims.exp;
  return {
    idToken: result.idToken,
    expiresOn: idTokenExp ? new Date(idTokenExp * 1000) : result.expiresOn,
    homeAccountId: result.account ? result.account.homeAccountId : '',
    returnTo: result.state || null,
  };
}

// Sivu siirtyy Microsoftille, joten lupaus ei normaalisti ratkea.
export async function loginWithMicrosoft() {
  await initMsal();
  await getInstance().loginRedirect({ ...loginRequest, prompt: 'select_account' });
}

// Uusi kirjautuminen, kun hiljainen uusinta ei onnistu (esim. refresh token vanhentunut ja
// kolmannen osapuolen evästeet estetty). Microsoftin oma istunto palauttaa yleensä suoraan takaisin
// samaan näkymään.
export async function reauthWithMicrosoft(homeAccountId) {
  await initMsal();
  const msal = getInstance();
  const account = findAccount(msal, homeAccountId);
  await msal.loginRedirect({
    ...loginRequest,
    state: window.location.hash,
    ...(account ? { account, loginHint: account.username } : {}),
  });
}

export async function acquireOidcTokenSilent(homeAccountId) {
  await initMsal();
  const msal = getInstance();
  const account = findAccount(msal, homeAccountId);
  if (!account) {
    throw new Error('No MSAL account available for silent token acquisition');
  }
  // forceRefresh: MSAL tarkistaa vain access tokenin vanhenemisen ja palauttaisi muuten välimuistista
  // vanhentuneen id_tokenin. Kutsutaan vain, kun id_token on vanhenemassa.
  const result = await msal.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
  return toLoginResult(result);
}

// Microsoftilta paluun käsittely; kutsutaan sovelluksen käynnistyksessä ennen istunnon tarkistusta.
export async function handleMsalRedirect() {
  if (!azureSsoEnabled) {
    return null;
  }
  await initMsal();
  const result = await getInstance().handleRedirectPromise();
  return result ? toLoginResult(result) : null;
}

// Tyhjentää vain paikallisen MSAL-välimuistin; Microsoft-istunto jää voimaan.
export async function msalLogout() {
  if (!azureSsoEnabled) {
    return;
  }
  try {
    await initMsal();
    await getInstance().clearCache();
  } catch (e) {
    console.error('MSAL cache clear failed', e);
  }
}
